import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import { Alert } from 'react-bootstrap';
import { useBrand } from '../context/BrandContext';
import '../styles/Settings.css';

const emptyPlatform = {
  openai_api_key: '',
  openai_model: 'gpt-4o',
  available_models: ['gpt-4o'],
  openai_api_key_masked: '',
  openai_api_key_set: false,
  smtp_host: '',
  smtp_port: 465,
  smtp_use_tls: false,
  smtp_use_ssl: true,
  smtp_username: '',
  smtp_password: '',
  smtp_password_set: false,
  smtp_password_masked: '',
  smtp_from_email: '',
  cloudinary_cloud_name: '',
  cloudinary_api_key: '',
  cloudinary_api_secret: '',
  cloudinary_api_key_set: false,
  cloudinary_api_key_masked: '',
  cloudinary_api_secret_set: false,
  cloudinary_api_secret_masked: '',
  master_password_set: false,
  master_password_masked: '',
};

const Settings = () => {
  const brand = useBrand();
  const [settings, setSettings] = useState({
    default_sender_name: '',
    default_sender_email: '',
    signature: '',
    email_footer: '',
  });
  const [prompts, setPrompts] = useState({
    step1_prompt: '',
    step2_prompt: '',
    step3_prompt: '',
  });
  const [platform, setPlatform] = useState(emptyPlatform);
  const [status, setStatus] = useState({ success: null, message: '' });
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [pwStatus, setPwStatus] = useState({ success: null, message: '' });
  const [masterPw, setMasterPw] = useState({
    current_master_password: '',
    new_master_password: '',
    confirm_master_password: '',
  });
  const [masterPwStatus, setMasterPwStatus] = useState({ success: null, message: '' });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'master_admin';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const requests = [
          axios.get('/auth/settings/'),
          axios.get('/auth/prompt-settings/'),
        ];
        if (isAdmin) {
          requests.push(axios.get('/auth/platform-settings/'));
        }
        const results = await Promise.allSettled(requests);
        const settingsRes = results[0].status === 'fulfilled' ? results[0].value : null;
        const promptRes = results[1].status === 'fulfilled' ? results[1].value : null;
        const platformRes = results[2] && results[2].status === 'fulfilled' ? results[2].value : null;
        if (settingsRes?.data) setSettings(settingsRes.data);
        if (promptRes?.data) setPrompts(promptRes.data);
        if (platformRes?.data) {
          setPlatform({
            ...emptyPlatform,
            ...platformRes.data,
            openai_api_key: '',
            smtp_password: '',
            cloudinary_api_key: '',
            cloudinary_api_secret: '',
          });
        }
        if (!settingsRes) {
          setStatus({ success: false, message: 'Failed to load settings' });
        }
      } catch (err) {
        console.error(err);
        setStatus({ success: false, message: 'Failed to load settings' });
      }
    };
    fetchSettings();
  }, [isAdmin]);

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handlePlatformChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPlatform({
      ...platform,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/auth/settings/', settings);
      setStatus({ success: true, message: 'Settings saved successfully' });
    } catch (err) {
      console.error(err);
      setStatus({ success: false, message: 'Failed to save settings' });
    }
  };

  const handlePromptSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/auth/prompt-settings/', prompts);
      setStatus({ success: true, message: 'Prompts updated successfully' });
    } catch (err) {
      console.error(err);
      setStatus({ success: false, message: 'Failed to update prompts' });
    }
  };

  const handlePlatformSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        openai_model: platform.openai_model,
        smtp_host: platform.smtp_host,
        smtp_port: platform.smtp_port,
        smtp_use_tls: platform.smtp_use_tls,
        smtp_use_ssl: platform.smtp_use_ssl,
        smtp_username: platform.smtp_username,
        smtp_from_email: platform.smtp_from_email,
        cloudinary_cloud_name: platform.cloudinary_cloud_name,
      };
      if (platform.openai_api_key?.trim()) {
        payload.openai_api_key = platform.openai_api_key.trim();
      }
      if (platform.smtp_password?.trim()) {
        payload.smtp_password = platform.smtp_password.trim();
      }
      if (platform.cloudinary_api_key?.trim()) {
        payload.cloudinary_api_key = platform.cloudinary_api_key.trim();
      }
      if (platform.cloudinary_api_secret?.trim()) {
        payload.cloudinary_api_secret = platform.cloudinary_api_secret.trim();
      }
      await axios.post('/auth/platform-settings/', payload);
      const refreshed = await axios.get('/auth/platform-settings/');
      setPlatform({
        ...emptyPlatform,
        ...refreshed.data,
        openai_api_key: '',
        smtp_password: '',
        cloudinary_api_key: '',
        cloudinary_api_secret: '',
      });
      setStatus({ success: true, message: 'Platform settings saved' });
    } catch (err) {
      setStatus({
        success: false,
        message: err.response?.data?.error || 'Failed to save platform settings',
      });
    }
  };

  const handlePwChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const handleMasterPwChange = (e) => {
    setMasterPw({ ...masterPw, [e.target.name]: e.target.value });
  };

  const handleMasterPasswordSubmit = async (e) => {
    e.preventDefault();
    if (masterPw.new_master_password !== masterPw.confirm_master_password) {
      setMasterPwStatus({ success: false, message: 'New master passwords do not match' });
      return;
    }
    if (masterPw.new_master_password.trim().length < 6) {
      setMasterPwStatus({ success: false, message: 'New master password must be at least 6 characters' });
      return;
    }
    try {
      await axios.post('/auth/platform-settings/', {
        current_master_password: masterPw.current_master_password,
        new_master_password: masterPw.new_master_password.trim(),
      });
      const refreshed = await axios.get('/auth/platform-settings/');
      setPlatform({
        ...emptyPlatform,
        ...refreshed.data,
        openai_api_key: '',
        smtp_password: '',
        cloudinary_api_key: '',
        cloudinary_api_secret: '',
      });
      setMasterPw({
        current_master_password: '',
        new_master_password: '',
        confirm_master_password: '',
      });
      setMasterPwStatus({ success: true, message: 'Master password updated' });
    } catch (err) {
      setMasterPwStatus({
        success: false,
        message: err.response?.data?.error || 'Failed to update master password',
      });
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) {
      setPwStatus({ success: false, message: 'New passwords do not match' });
      return;
    }
    try {
      await axios.post('/auth/change-password/', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      setPwStatus({ success: true, message: 'Password changed successfully' });
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwStatus({ success: false, message: err.response?.data?.error || 'Password change failed' });
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your account and platform configuration</p>
      </div>

      {status.message && (
        <Alert variant={status.success ? 'success' : 'danger'} className="settings-alert">
          {status.message}
        </Alert>
      )}

      {isAdmin && (
        <>
          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-robot"></i>
              <h2>OpenAI Configuration</h2>
            </div>
            <form onSubmit={handlePlatformSubmit} className="settings-form">
              <div className="form-group">
                <label>OpenAI API Key</label>
                <input
                  type="password"
                  name="openai_api_key"
                  value={platform.openai_api_key}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder={
                    platform.openai_api_key_set
                      ? `Saved: ${platform.openai_api_key_masked} — enter new key to replace`
                      : 'sk-...'
                  }
                  autoComplete="off"
                />
                <small style={{ color: '#64748b' }}>
                  Leave blank to keep the current key. Analyses use this key and the selected model.
                </small>
              </div>

              <div className="form-group">
                <label>Preferred Model</label>
                <select
                  name="openai_model"
                  value={platform.openai_model}
                  onChange={handlePlatformChange}
                  className="form-control"
                >
                  {(platform.available_models || ['gpt-4o']).map((model) => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>

              <button type="submit" className="btn btn-primary">Save AI Settings</button>
            </form>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-server"></i>
              <h2>SMTP Email Configuration</h2>
            </div>
            <form onSubmit={handlePlatformSubmit} className="settings-form">
              <div className="form-group">
                <label>SMTP Host</label>
                <input
                  type="text"
                  name="smtp_host"
                  value={platform.smtp_host}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder="smtp.example.com"
                />
              </div>

              <div className="form-group">
                <label>SMTP Port</label>
                <input
                  type="number"
                  name="smtp_port"
                  value={platform.smtp_port}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder="465"
                />
              </div>

              <div className="form-group" style={{ flexDirection: 'row', gap: 24, alignItems: 'center' }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: 0 }}>
                  <input
                    type="checkbox"
                    name="smtp_use_ssl"
                    checked={!!platform.smtp_use_ssl}
                    onChange={handlePlatformChange}
                  />
                  Use SSL
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: 0 }}>
                  <input
                    type="checkbox"
                    name="smtp_use_tls"
                    checked={!!platform.smtp_use_tls}
                    onChange={handlePlatformChange}
                  />
                  Use TLS
                </label>
              </div>

              <div className="form-group">
                <label>SMTP Username</label>
                <input
                  type="text"
                  name="smtp_username"
                  value={platform.smtp_username}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder="info@company.com"
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label>SMTP Password</label>
                <input
                  type="password"
                  name="smtp_password"
                  value={platform.smtp_password}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder={
                    platform.smtp_password_set
                      ? `Saved: ${platform.smtp_password_masked} — enter new password to replace`
                      : 'SMTP password'
                  }
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label>Default From Email</label>
                <input
                  type="text"
                  name="smtp_from_email"
                  value={platform.smtp_from_email}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder="Company Name <info@company.com>"
                />
              </div>

              <button type="submit" className="btn btn-primary">Save SMTP Settings</button>
            </form>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-cloud"></i>
              <h2>Cloudinary Configuration</h2>
            </div>
            <form onSubmit={handlePlatformSubmit} className="settings-form">
              <div className="form-group">
                <label>Cloud name</label>
                <input
                  type="text"
                  name="cloudinary_cloud_name"
                  value={platform.cloudinary_cloud_name}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder="CLOUDINARY_CLOUD_NAME"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>API key</label>
                <input
                  type="password"
                  name="cloudinary_api_key"
                  value={platform.cloudinary_api_key}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder={
                    platform.cloudinary_api_key_set
                      ? `Saved: ${platform.cloudinary_api_key_masked} — enter new key to replace`
                      : 'CLOUDINARY_API_KEY'
                  }
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>API secret</label>
                <input
                  type="password"
                  name="cloudinary_api_secret"
                  value={platform.cloudinary_api_secret}
                  onChange={handlePlatformChange}
                  className="form-control"
                  placeholder={
                    platform.cloudinary_api_secret_set
                      ? `Saved: ${platform.cloudinary_api_secret_masked} — enter new secret to replace`
                      : 'CLOUDINARY_API_SECRET'
                  }
                  autoComplete="new-password"
                />
                <small style={{ color: '#64748b' }}>
                  Required for support ticket attachments and for broadcast files larger than 10 MB. Leave key/secret blank to keep the saved values.
                </small>
              </div>
              <button type="submit" className="btn btn-primary">Save Cloudinary Settings</button>
            </form>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-brain"></i>
              <h2>AI Prompt Configuration</h2>
            </div>

            <form onSubmit={handlePromptSubmit} className="settings-form">
              {['step1_prompt', 'step2_prompt', 'step3_prompt'].map((key) => (
                <div className="form-group" key={key}>
                  <label>{key.replace('_', ' ').toUpperCase()}</label>
                  <textarea
                    name={key}
                    rows={key === 'step2_prompt' ? 12 : 6}
                    value={prompts[key]}
                    onChange={(e) => setPrompts({ ...prompts, [key]: e.target.value })}
                    className="form-control"
                  />
                </div>
              ))}
              <button type="submit" className="btn btn-secondary">Update Prompts</button>
            </form>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-envelope"></i>
              <h2>Email Branding</h2>
            </div>

            <form onSubmit={handleSubmit} className="settings-form">
              <div className="form-group">
                <label>Default Sender Name</label>
                <input
                  type="text"
                  name="default_sender_name"
                  value={settings.default_sender_name}
                  onChange={handleChange}
                  placeholder={`e.g. ${brand.companyName}`}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Default Sender Email</label>
                <input
                  type="email"
                  name="default_sender_email"
                  value={settings.default_sender_email}
                  onChange={handleChange}
                  placeholder={`e.g. ${brand.supportEmail || 'hello@company.com'}`}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Email Signature</label>
                <textarea
                  name="signature"
                  rows={4}
                  value={settings.signature}
                  onChange={handleChange}
                  placeholder="Name\nTitle — Company\nPhone"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Email Footer</label>
                <textarea
                  name="email_footer"
                  rows={2}
                  value={settings.email_footer}
                  onChange={handleChange}
                  placeholder={`${brand.companyName} | www.example.com`}
                  className="form-control"
                />
              </div>

              <button type="submit" className="btn btn-primary">Save Branding</button>
            </form>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-key"></i>
              <h2>Master Password</h2>
            </div>
            <p style={{ color: '#64748b', marginTop: 0 }}>
              Used for registration and privileged API actions. This is not your login password.
              {platform.master_password_set
                ? ` Current value is saved as ${platform.master_password_masked}.`
                : ' No master password is set yet.'}
            </p>
            {masterPwStatus.message && (
              <Alert variant={masterPwStatus.success ? 'success' : 'danger'} className="settings-alert">
                {masterPwStatus.message}
              </Alert>
            )}
            <form onSubmit={handleMasterPasswordSubmit} className="settings-form">
              <div className="form-group">
                <label>Current Master Password</label>
                <input
                  type="password"
                  name="current_master_password"
                  value={masterPw.current_master_password}
                  onChange={handleMasterPwChange}
                  required={!!platform.master_password_set}
                  className="form-control"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>New Master Password</label>
                <input
                  type="password"
                  name="new_master_password"
                  value={masterPw.new_master_password}
                  onChange={handleMasterPwChange}
                  required
                  minLength={6}
                  className="form-control"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Master Password</label>
                <input
                  type="password"
                  name="confirm_master_password"
                  value={masterPw.confirm_master_password}
                  onChange={handleMasterPwChange}
                  required
                  minLength={6}
                  className="form-control"
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="btn btn-warning">Update Master Password</button>
            </form>
          </div>
        </>
      )}

      <hr className="section-divider" />

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-lock"></i>
          <h2>Change Password</h2>
        </div>

        {pwStatus.message && (
          <Alert variant={pwStatus.success ? 'success' : 'danger'} className="settings-alert">
            {pwStatus.message}
          </Alert>
        )}

        <form onSubmit={handlePasswordSubmit} className="settings-form">
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              name="current_password"
              value={passwords.current_password}
              onChange={handlePwChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              name="new_password"
              value={passwords.new_password}
              onChange={handlePwChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              name="confirm_password"
              value={passwords.confirm_password}
              onChange={handlePwChange}
              required
              className="form-control"
            />
          </div>

          <button type="submit" className="btn btn-warning">Update Password</button>
        </form>
      </div>
    </div>
  );
};

export default Settings;
